// Explicit .ts extensions: this module is unit-tested by Node directly.
import { STATIONS } from './stations.ts';
import type { Station } from './types.ts';

/**
 * Filtering the station list by what a station actually measures.
 *
 * Sensor coverage is patchy — water temperature exists at 5 of the 18 — so
 * finding one otherwise means opening stations until one has the panel. Kept
 * behind a closed disclosure because most visits are about wind and never need it.
 */
export interface StationFilter {
  key: string;
  label: string;
  matches: (s: Station) => boolean;
}

export const FILTERS: StationFilter[] = [
  { key: 'buoy', label: 'Moored buoys', matches: (s) => s.kind === 'buoy' },
  /*
   * Wind narrows nothing — every station reports it — and is here to be
   * *labelled*, not filtered. Without it, choosing water temperature replaced
   * the wind numbers on the map with no way to get them back short of clearing
   * the filter, which on a wind site is the one comparison you cannot lose.
   */
  { key: 'wind', label: 'Wind', matches: () => true },
  { key: 'waterTemp', label: 'Water temperature', matches: (s) => Boolean(s.sensors.waterTemp) },
  { key: 'airTemp', label: 'Air temperature', matches: (s) => Boolean(s.sensors.airTemp) },
  { key: 'waterLevel', label: 'Water level', matches: (s) => Boolean(s.sensors.waterLevel) },
  { key: 'waves', label: 'Waves', matches: (s) => Boolean(s.sensors.waves) },
];

/**
 * A station passes when it satisfies every active filter.
 *
 * AND rather than OR: "water temperature and waves" should mean stations having
 * both, which is the question worth asking. An empty set matches everything.
 */
export function matchesFilters(station: Station, active: ReadonlySet<string>): boolean {
  if (active.size === 0) return true;
  return FILTERS.every((f) => !active.has(f.key) || f.matches(station));
}

export function filterStations(active: ReadonlySet<string>): Station[] {
  return STATIONS.filter((s) => matchesFilters(s, active));
}

/**
 * How many stations each filter would leave, given the others already active.
 *
 * Counting against the current selection rather than the whole set means a
 * choice showing "0" genuinely leads nowhere, so the reader can see a dead end
 * before clicking it.
 */
export function counts(active: ReadonlySet<string>): Record<string, number> {
  const out: Record<string, number> = {};
  for (const f of FILTERS) {
    const probe = new Set(active);
    probe.add(f.key);
    out[f.key] = STATIONS.filter((s) => matchesFilters(s, probe)).length;
  }
  return out;
}
